import { useEffect, useRef, useState } from "react";
import { useI18n } from "./i18n.jsx";
import "./twin-deletion.css";

export function DeleteTwinDialog({ name, twinId, onClose, onDelete, onDeleteEvents, onRemoveBinding }) {
  const { t } = useI18n();
  const [confirmed, setConfirmed] = useState(false), [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState(false), [uncertain, setUncertain] = useState(false), [error, setError] = useState("");
  const box = useRef(null), running = useRef(false), receipt = useRef(null);
  const [eventsDeleted, setEventsDeleted] = useState(false);
  async function clearEvents() {
    if (!confirmed || running.current) return;
    running.current = true; setBusy(true); setError("");
    try {
      const result = await onDeleteEvents();
      if (!result?.eventsDeleted) throw new Error("OBJECTID_EVENTS_CLEANUP_REQUIRED");
      setEventsDeleted(true);
    } catch (cause) { setError(t(cause.message)); }
    finally { running.current = false; setBusy(false); }
  }
  useEffect(() => {
    const previous = document.activeElement;
    box.current.querySelector("button")?.focus();
    return () => previous?.focus?.();
  }, []);
  async function submit(event) {
    event.preventDefault();
    if (running.current || uncertain || (onDeleteEvents && !eventsDeleted) || (!confirmed && !receipt.current)) return;
    running.current = true; setBusy(true); setError("");
    try {
      if (!receipt.current) {
        receipt.current = await onDelete();
        if (receipt.current?.deleted !== true) throw Object.assign(new Error("TWIN_DELETE_OUTCOME_UNKNOWN"), { outcomeUnknown: true });
        setDeleted(true);
      }
      // Persistence retries must never submit a second IOTA deletion.
      await onRemoveBinding();
    } catch (cause) {
      if (cause.outcomeUnknown || !cause.status && !receipt.current) setUncertain(true);
      setError(t(cause.message));
    } finally { running.current = false; setBusy(false); }
  }
  function keydown(event) {
    if (event.key === "Escape" && !busy && !deleted) { event.preventDefault(); onClose(); }
    if (event.key === "Tab") {
      const items = [...box.current.querySelectorAll("button:not(:disabled),input:not(:disabled)")];
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }
  return <div className="modal-backdrop"><section ref={box} className="modal twin-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-twin-title" onKeyDown={keydown}>
    <h2 id="delete-twin-title">{t("deleteTwin")}</h2><p>{t("deleteTwinWarning")}</p><strong>{name}</strong><code className="delete-twin-id">{twinId}</code>
    {deleted && <p role="status">{t("twinDeletedSavePending")}</p>}
    {uncertain && <p role="alert">{t("TWIN_DELETE_OUTCOME_UNKNOWN")}</p>}
    <form onSubmit={submit}>
      {!deleted && <label className="delete-twin-confirm"><input type="checkbox" checked={confirmed} disabled={busy || uncertain} onChange={e => setConfirmed(e.target.checked)} />{t("deleteTwinAcknowledge")}</label>}
      {error && <p className="form-error" role="alert">{error}</p>}
      {onDeleteEvents && <section><p>{t("bulkEventsWarning")}</p><button type="button" className="delete-twin" disabled={busy || !confirmed || eventsDeleted} onClick={clearEvents}>{t(eventsDeleted ? "bulkEventsDone" : "bulkEventsDelete")}</button></section>}
      <div className="modal-actions"><button type="button" className="ghost" disabled={busy} onClick={onClose}>{t(deleted || uncertain ? "close" : "cancel")}</button><button type="submit" className="delete-twin" disabled={busy || uncertain || (onDeleteEvents && !eventsDeleted) || !confirmed && !deleted}>{t(busy ? "saving" : deleted ? "retryTwinBindingSave" : "confirmDeleteTwin")}</button></div>
    </form>
  </section></div>;
}
